import { SearchResultFacetValue } from "@made-people/grease-search";
import { z } from "zod";
import { registerColorFacet } from "./mm-color-facet-mapper";

const facetImageDataSchema = z.object({
  image: z
    .object({
      url: z.string(),
    })
    .optional(),
  description: z.string(),
});

type FacetImageData = z.infer<typeof facetImageDataSchema>;

/**
 * Try to parse a string as JSON and validate it against our MM color swatch schema
 */
const tryParseSwatchImageFacetData = (
  str: string,
): FacetImageData | undefined => {
  try {
    const parsed = JSON.parse(str);
    const result = facetImageDataSchema.safeParse(parsed);
    return result.success ? result.data : undefined;
  } catch (e) {
    // Silent fail on parsing error
    return undefined;
  }
};

/**
 * Custom mapper for Miss Mary algolia facets
 * Transforms algolia facet values to Grease facet values with custom logic
 */
export const mmAlgoliaFacetToGreaseFacetValue = (
  key: string,
  value: string,
  count: number,
  chosenFacets: string[],
): SearchResultFacetValue => {
  if (key === "color") {
    // If key is color, try to parse the value as JSON based on MM color swatch structure
    const parsedValue = tryParseSwatchImageFacetData(value);

    if (!parsedValue) {
      return {
        id: value,
        name: value,
        selected: chosenFacets.includes(`${key}:${value}`),
        disabled: false,
        count,
      };
    }
    const imageUrl = parsedValue.image?.url || "";
    const description = parsedValue.description || value;

    // Register the mapping between clean description and full JSON
    const cleanId = registerColorFacet(value);

    return {
      id: cleanId,
      name: description,
      selected: chosenFacets.includes(`${key}:${value}`),
      disabled: false,
      count,
      image: imageUrl,
    };
  }

  return {
    id: value,
    name: value,
    selected: chosenFacets.includes(`${key}:${value}`),
    disabled: false,
    count,
  };
};
